import { memo, useMemo } from 'react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useOverviewData } from './useOverviewData';
import { ChartWrapper } from '../shared/ChartWrapper';

/** Blend a hex color toward white. amount=0 → original, amount=1 → white */
function lighten(hex, amount) {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.round(((n >> 16) & 0xff) + (255 - ((n >> 16) & 0xff)) * amount);
  const g = Math.round(((n >> 8) & 0xff) + (255 - ((n >> 8) & 0xff)) * amount);
  const b = Math.round((n & 0xff) + (255 - (n & 0xff)) * amount);
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}

// Type definitions: display name → data key in stats.byTypeAndFunding
const OUTPUT_TYPES = [
  { name: 'Knowledge Products', key: 'Knowledge product' },
  { name: 'Capacity Sharing', key: 'Capacity sharing for development' },
  { name: 'Innovation Dev', key: 'Innovation development' },
  { name: 'Other Output', key: 'Other output' },
];
const OUTCOME_TYPES = [
  { name: 'Innovation Use', key: 'Innovation use' },
  { name: 'Policy Change', key: 'Policy change' },
  { name: 'Other Outcome', key: 'Other outcome' },
  { name: 'Capacity Change', key: 'Capacity change' },
  { name: 'Innovation Packages', key: 'Innovation Package' },
];
const IMPACT_TYPES = [
  { name: 'Impact Contributions', key: 'Impact contribution' },
];

const OUTPUT_COLORS = { 'Knowledge Products': '#b45309', 'Capacity Sharing': '#a16207', 'Innovation Dev': '#92400e', 'Other Output': '#854d0e' };
const OUTCOME_COLORS = { 'Innovation Use': '#be123c', 'Policy Change': '#9f1239', 'Other Outcome': '#881337', 'Capacity Change': '#a21caf', 'Innovation Packages': '#7e22ce' };
const IMPACT_COLOR = '#7e22ce';

const W3_LIGHTEN = 0.5;
const GAP_FRACTION = 0.01; // Gap = 1% of ring total

/** Build ring data with W1/W2 + W3 sub-segments for a set of types, with spacers between categories */
function buildRingData(types, colorMap, defaultColor, byTypeAndFunding, byType) {
  const data = [];
  const colors = {};

  // First pass: compute ring total for proportional gap sizing
  let ringTotal = 0;
  for (const { key } of types) {
    ringTotal += byType?.[key] || 0;
  }
  const gapSize = Math.max(1, Math.round(ringTotal * GAP_FRACTION));

  for (const { name, key } of types) {
    const baseColor = colorMap?.[name] ?? defaultColor;
    const w3Color = lighten(baseColor, W3_LIGHTEN);
    const funding = byTypeAndFunding?.[key];
    let emitted = false;

    if (funding) {
      const w1w2 = funding.W1W2 || 0;
      const w3 = funding.W3Bilateral || 0;
      if (w1w2 > 0) {
        const label = `${name} (W1/W2)`;
        data.push({ name: label, baseName: name, funding: 'W1/W2', value: w1w2 });
        colors[label] = baseColor;
        emitted = true;
      }
      if (w3 > 0) {
        const label = `${name} (W3)`;
        data.push({ name: label, baseName: name, funding: 'W3/Bilateral', value: w3 });
        colors[label] = w3Color;
        emitted = true;
      }
    } else {
      const total = byType?.[key] || 0;
      if (total > 0) {
        data.push({ name, baseName: name, funding: null, value: total });
        colors[name] = baseColor;
        emitted = true;
      }
    }

    // Insert spacer after each category that emitted data
    if (emitted) {
      const spacerName = `__spacer_${name}`;
      data.push({ name: spacerName, baseName: '__spacer', value: gapSize, isSpacer: true });
      colors[spacerName] = 'transparent';
    }
  }

  // Remove trailing spacer
  if (data.length > 0 && data[data.length - 1].isSpacer) {
    const removed = data.pop();
    delete colors[removed.name];
  }

  return { data, colors };
}

export const TocDiagram = memo(function TocDiagram({ stats: statsProp }) {
  const { stats: statsHook, byTypeAndFunding: btfHook } = useOverviewData();
  const stats = statsProp ?? statsHook;
  const byTypeAndFunding = stats?.byTypeAndFunding ?? btfHook;

  // Build ring data with funding split
  const output = useMemo(() => buildRingData(OUTPUT_TYPES, OUTPUT_COLORS, null, byTypeAndFunding, stats?.byType), [byTypeAndFunding, stats?.byType]);
  const outcome = useMemo(() => buildRingData(OUTCOME_TYPES, OUTCOME_COLORS, null, byTypeAndFunding, stats?.byType), [byTypeAndFunding, stats?.byType]);
  const impact = useMemo(() => buildRingData(IMPACT_TYPES, null, IMPACT_COLOR, byTypeAndFunding, stats?.byType), [byTypeAndFunding, stats?.byType]);

  // Legend rows (totals per type, with W1/W2 + W3 breakdown)
  const outputRows = OUTPUT_TYPES.map(({ name, key }) => {
    const f = byTypeAndFunding?.[key];
    return { name, value: stats?.byType?.[key] || 0, w1w2: f?.W1W2 || 0, w3: f?.W3Bilateral || 0 };
  });
  const outcomeRows = OUTCOME_TYPES.map(({ name, key }) => {
    const f = byTypeAndFunding?.[key];
    return { name, value: stats?.byType?.[key] || 0, w1w2: f?.W1W2 || 0, w3: f?.W3Bilateral || 0 };
  });
  const impactRow = (() => {
    const f = byTypeAndFunding?.['Impact contribution'];
    return { name: 'Impact Contributions', value: stats?.byType?.['Impact contribution'] || 0, w1w2: f?.W1W2 || 0, w3: f?.W3Bilateral || 0 };
  })();

  const totalOutputs = outputRows.reduce((s, r) => s + r.value, 0);
  const totalOutcomes = outcomeRows.reduce((s, r) => s + r.value, 0);
  const impactContribs = impactRow.value;
  const totalResults = totalOutputs + totalOutcomes + impactContribs;

  // Summary cards
  const partnerships = stats?.partners?.length || 0;
  const impactAreaCount = stats?.byImpactArea ? Object.keys(stats.byImpactArea).filter(k => {
    const scores = stats.byImpactArea[k];
    return (scores?.[1] || 0) > 0 || (scores?.[2] || 0) > 0;
  }).length : 0;

  // Downloadable chart data
  const chartData = useMemo(() => {
    if (!stats) return [];
    const allTypes = [...OUTPUT_TYPES.map(t => ({ sphere: 'Outputs', ...t })), ...OUTCOME_TYPES.map(t => ({ sphere: 'Outcomes', ...t })), ...IMPACT_TYPES.map(t => ({ sphere: 'Impact', ...t }))];
    return allTypes.map(({ sphere, name, key }) => {
      const f = byTypeAndFunding?.[key];
      return { sphere, type: name, total: stats?.byType?.[key] || 0, 'W1/W2': f?.W1W2 || 0, 'W3/Bilateral': f?.W3Bilateral || 0 };
    }).filter(d => d.total > 0);
  }, [stats, byTypeAndFunding]);

  const fmt = (n) => n.toLocaleString();

  const SunburstTooltip = ({ active, payload }) => {
    if (!active || !payload?.[0]) return null;
    const d = payload[0].payload;
    if (d.isSpacer) return null;
    const baseName = d.baseName || d.name;
    const sphere = OUTPUT_COLORS[baseName] ? 'Outputs' : OUTCOME_COLORS[baseName] ? 'Outcomes' : 'Impact';
    const sphereTotal = sphere === 'Outputs' ? totalOutputs : sphere === 'Outcomes' ? totalOutcomes : impactContribs;
    const pct = sphereTotal > 0 ? ((d.value / sphereTotal) * 100).toFixed(1) : '0.0';
    const fundingLabel = d.funding ? ` — ${d.funding}` : '';
    return (
      <div style={{ background: '#fff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: '13px' }}>
        <div style={{ fontWeight: 600, marginBottom: 2 }}>{baseName}{fundingLabel}</div>
        <div style={{ color: '#6b7280' }}>{fmt(d.value)} results &middot; {pct}% of {sphere}</div>
      </div>
    );
  };

  const LegendGroup = ({ title, subtitle, rows, colorMap, total, borderColor, titleColor }) => (
    <div className="flex-1 min-w-[140px]">
      <div className="text-xs font-semibold mb-1.5" style={{ color: titleColor }}>{title}</div>
      <div className="text-[11px] text-gray-500 mb-2">{subtitle}</div>
      <div className="space-y-1.5">
        {rows.map(({ name, value, w1w2, w3 }) => (
          <div key={name} className={`text-xs ${value === 0 ? 'opacity-40' : ''}`}>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: colorMap[name] }} />
              <span className="text-gray-700 flex-1">{name}</span>
              <span className="font-medium tabular-nums text-gray-900">{fmt(value)}</span>
            </div>
            {value > 0 && (w1w2 > 0 || w3 > 0) && (
              <div className="ml-[18px] text-[10px] text-gray-400">
                {w1w2 > 0 && <span>W1/W2: {fmt(w1w2)}</span>}
                {w1w2 > 0 && w3 > 0 && <span> · </span>}
                {w3 > 0 && <span>W3: {fmt(w3)}</span>}
              </div>
            )}
          </div>
        ))}
      </div>
      {total != null && (
        <div className="mt-1.5 pt-1.5 text-xs font-bold flex justify-between" style={{ borderTop: `1px solid ${borderColor}` }}>
          <span style={{ color: titleColor }}>Total</span>
          <span className="tabular-nums">{fmt(total)}</span>
        </div>
      )}
    </div>
  );

  return (
    <ChartWrapper title="Theory of Change Alignment" info="Sunburst showing how reported results align with the program's Theory of Change across output, outcome, and impact levels." chartData={chartData} exportFilename="toc-alignment">
      <h4 className="font-medium text-gray-900 mb-4" style={{display: 'none'}}>Theory of Change Overview</h4>

      {/* Sunburst chart */}
      <div className="flex justify-center">
        <div className="relative w-full max-w-[400px] mx-auto aspect-square">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Tooltip content={<SunburstTooltip />} />
              <Pie data={output.data} dataKey="value" cx="50%" cy="50%" innerRadius={50} outerRadius={100} isAnimationActive={false}>
                {output.data.map(d => <Cell key={d.name} fill={output.colors[d.name]} stroke={d.isSpacer ? 'none' : '#fff'} strokeWidth={d.isSpacer ? 0 : 1} />)}
              </Pie>
              <Pie data={outcome.data} dataKey="value" cx="50%" cy="50%" innerRadius={108} outerRadius={148} isAnimationActive={false}>
                {outcome.data.map(d => <Cell key={d.name} fill={outcome.colors[d.name]} stroke={d.isSpacer ? 'none' : '#fff'} strokeWidth={d.isSpacer ? 0 : 1} />)}
              </Pie>
              <Pie data={impact.data} dataKey="value" cx="50%" cy="50%" innerRadius={156} outerRadius={180} isAnimationActive={false}>
                {impact.data.map(d => <Cell key={d.name} fill={impact.colors[d.name]} stroke={d.isSpacer ? 'none' : '#fff'} strokeWidth={d.isSpacer ? 0 : 1} />)}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#111827', lineHeight: 1 }}>{fmt(totalResults)}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Results</div>
          </div>
        </div>
      </div>

      {/* Ring labels */}
      <div className="flex justify-center gap-4 mt-1 mb-1 text-[11px] text-gray-500">
        <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ backgroundColor: '#b45309' }} />Inner: Outputs</span>
        <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ backgroundColor: '#be123c' }} />Middle: Outcomes</span>
        <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ backgroundColor: '#7e22ce' }} />Outer: Impact</span>
      </div>
      <div className="flex justify-center mb-4 text-[10px] text-gray-400">
        Solid = W1/W2 (Pooled) · Light = W3/Bilateral
      </div>

      {/* Legend — 3 groups */}
      <div className="flex flex-col md:flex-row gap-4 md:gap-6 px-2">
        <LegendGroup title="Sphere of Control" subtitle="Outputs" rows={outputRows} colorMap={OUTPUT_COLORS} total={totalOutputs} borderColor="#d6a356" titleColor="#92400e" />
        <LegendGroup title="Sphere of Influence" subtitle="Outcomes" rows={outcomeRows} colorMap={OUTCOME_COLORS} total={totalOutcomes} borderColor="#e8a0b0" titleColor="#9f1239" />
        <LegendGroup title="Sphere of Interest" subtitle="Impact" rows={[impactRow]} colorMap={{ 'Impact Contributions': IMPACT_COLOR }} total={null} borderColor="#c4a8e0" titleColor="#6b21a8" />
      </div>

      {/* Summary stats row */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3 text-center">
        <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
          <div className="text-xl font-bold text-amber-800">{fmt(totalOutputs)}</div>
          <div className="text-xs text-amber-600">Outputs</div>
        </div>
        <div className="bg-rose-50 rounded-lg p-3 border border-rose-200">
          <div className="text-xl font-bold text-rose-800">{fmt(totalOutcomes)}</div>
          <div className="text-xs text-rose-600">Outcomes</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
          <div className="text-xl font-bold text-purple-800">{fmt(impactContribs)}</div>
          <div className="text-xs text-purple-600">Impact Contribs</div>
        </div>
        <div className="bg-green-50 rounded-lg p-3 border border-green-200">
          <div className="text-xl font-bold text-green-800">{fmt(partnerships)}</div>
          <div className="text-xs text-green-600">Partnerships</div>
        </div>
        <div className="bg-blue-50 rounded-lg p-3 border border-blue-200 col-span-2 md:col-span-1">
          <div className="text-xl font-bold text-blue-800">{impactAreaCount}</div>
          <div className="text-xs text-blue-600">Impact Areas</div>
        </div>
      </div>
    </ChartWrapper>
  );
});
